/* eslint-disable @typescript-eslint/no-explicit-any */
import { type FC, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { DataTable } from '@/components/data-table/DataTable'
import { adminUserColumns } from '@/components/internal-users/AdminUserColumns'
import { roleColumns } from '@/components/internal-users/RoleColumns'
import {
    AddUserModal,
    type AddUserFormState,
} from '@/components/internal-users/AddUserModal'
import { AddRoleModal } from '@/components/internal-users/AddRoleModal'
import { PermissionSettingsModal } from '@/components/internal-users/PermissionSettingsModal'
import { InternalUsersFilterDialog } from '@/components/internal-users/InternalUsersFilterDialog'
import { InternalRolesFilterDialog } from '@/components/internal-users/InternalRolesFilterDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
    useCreateInternalRole,
    useCreateInternalUser,
    useDeleteInternalRole,
    useInternalRolePermissions,
    useInternalRole,
    useInternalRoles,
    useInternalUsers,
    useRevokeInternalUserAccess,
    useUpdateInternalRole,
    type InternalRole,
    type InternalUser,
} from '@/hooks/useInternalUsers'
import type {
    InternalRoleFilters,
    InternalRolePermission,
    InternalUserFilters,
} from '@/types/internal'
import {
    AlertTriangle,
    Eye,
    ShieldOff,
    Trash2,
    UserPlus,
    Wrench,
} from 'lucide-react'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'

type TabKey = 'users' | 'roles'

const statusStyles: Record<InternalUser['status'], string> = {
    Active: 'bg-green-100 text-green-700 hover:bg-green-100',
    Inactive: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
    Suspended: 'bg-red-100 text-red-700 hover:bg-red-100',
}

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as {
            message?: string
            errors?: string[]
        }
        const message = data?.message || error.message
        const errors = data?.errors

        // If there are errors in the array, append them to the message
        if (errors && Array.isArray(errors) && errors.length > 0) {
            return `${message}\n${errors.join('\n')}`
        }

        return message
    }
    return error instanceof Error
        ? error.message
        : 'An unexpected error occurred'
}

const normalizePermissions = (
    permissions?: (string | InternalRolePermission)[] | null
): InternalRolePermission[] => {
    if (!Array.isArray(permissions)) return []

    return permissions
        .map(permission => {
            if (typeof permission === 'string') {
                return {
                    id: permission,
                    key: permission,
                    name: permission,
                    description: null,
                }
            }

            const fallbackId =
                permission.id || permission.key || permission.name || ''
            if (!fallbackId) return null

            return {
                id: fallbackId,
                key: permission.key || fallbackId,
                name: permission.name || fallbackId,
                description:
                    permission.description === undefined
                        ? null
                        : permission.description,
            }
        })
        .filter(
            (permission): permission is InternalRolePermission =>
                permission !== null
        )
}

const ConfirmDialog: FC<{
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    description: string
    confirmLabel: string
    onConfirm: () => void
    isLoading?: boolean
    destructive?: boolean
}> = ({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel,
    onConfirm,
    isLoading = false,
    destructive = false,
}) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[480px]">
            <div className="flex items-start gap-3">
                <div
                    className={`rounded-full p-2 ${
                        destructive ? 'bg-red-100' : 'bg-[#E5F5F9]'
                    }`}
                >
                    <AlertTriangle
                        className={`h-5 w-5 ${
                            destructive ? 'text-red-600' : 'text-[#0F172A]'
                        }`}
                    />
                </div>
                <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-[#0F172A]">
                        {title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        {description}
                    </p>
                </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
                <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant={destructive ? 'destructive' : 'default'}
                    disabled={isLoading}
                >
                    {isLoading ? 'Processing...' : confirmLabel}
                </Button>
            </div>
        </DialogContent>
    </Dialog>
)

const UserPreviewModal: FC<{
    user: InternalUser | null
    open: boolean
    onOpenChange: (open: boolean) => void
}> = ({ user, open, onOpenChange }) => {
    if (!user) return null

    const roleLabel = user.internalRoleTitle || user.role || '—'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="text-lg font-semibold text-[#0F172A]">
                            View account
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Quick summary of this admin user.
                        </p>
                    </div>
                    <Badge
                        variant="secondary"
                        className={
                            statusStyles[user.status] ?? statusStyles.Active
                        }
                    >
                        {user.status}
                    </Badge>
                </div>
                <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                    <div>
                        <p className="text-xs text-muted-foreground">User ID</p>
                        <p className="font-semibold">#{user.id}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Role</p>
                        <p className="font-semibold">{roleLabel}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">
                            Full name
                        </p>
                        <p className="font-semibold">{user.fullName}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-semibold">{user.email}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-semibold">{user.phoneNumber}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">
                            Date added
                        </p>
                        <p className="font-semibold">{user.dateAdded}</p>
                    </div>
                    {user.country && (
                        <div>
                            <p className="text-xs text-muted-foreground">
                                Country
                            </p>
                            <p className="font-semibold">{user.country}</p>
                        </div>
                    )}
                    {user.residentialAddress && (
                        <div>
                            <p className="text-xs text-muted-foreground">
                                Address
                            </p>
                            <p className="font-semibold">
                                {user.residentialAddress}
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

const renderTableSkeleton = () => (
    <div className="space-y-3">
        <div className="flex items-center justify-between">
            <Skeleton className="h-10 w-[260px]" />
            <Skeleton className="h-10 w-[120px]" />
        </div>
        <div className="rounded-md border">
            <Skeleton className="h-12 w-full rounded-t-md" />
            {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
            ))}
        </div>
        <div className="flex items-center justify-end">
            <Skeleton className="h-8 w-[200px]" />
        </div>
    </div>
)

const InternalUsers: FC = () => {
    const [activeTab, setActiveTab] = useState<TabKey>('users')
    const [selectedRole, setSelectedRole] = useState<InternalRole | null>(null)
    const [selectedUser, setSelectedUser] = useState<InternalUser | null>(null)
    const [viewUserOpen, setViewUserOpen] = useState(false)
    const [isAddUserOpen, setIsAddUserOpen] = useState(false)
    const [isAddRoleOpen, setIsAddRoleOpen] = useState(false)
    const [isPermissionOpen, setIsPermissionOpen] = useState(false)
    const [editingRole, setEditingRole] = useState<InternalRole | null>(null)
    const [roleToDelete, setRoleToDelete] = useState<InternalRole | null>(null)
    const [usersToRevoke, setUsersToRevoke] = useState<InternalUser[]>([])
    const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false)
    const [isUserFilterDialogOpen, setIsUserFilterDialogOpen] = useState(false)
    const [isRoleFilterDialogOpen, setIsRoleFilterDialogOpen] = useState(false)

    const [userFilters, setUserFilters] = useState<InternalUserFilters>({
        page: 1,
        limit: 20,
    })
    const [roleFilters, setRoleFilters] = useState<InternalRoleFilters>({
        page: 1,
        limit: 20,
    })

    const {
        data: internalUsersData,
        isLoading: isLoadingUsers,
        error: usersError,
        refetch: refetchUsers,
    } = useInternalUsers(userFilters)
    const {
        data: internalRolesData,
        isLoading: isLoadingRoles,
        error: rolesError,
        refetch: refetchRoles,
    } = useInternalRoles(roleFilters)
    const { data: permissionsData } = useInternalRolePermissions()

    const createUserMutation = useCreateInternalUser()
    const revokeAccessMutation = useRevokeInternalUserAccess()
    const createRoleMutation = useCreateInternalRole()
    const updateRoleMutation = useUpdateInternalRole()
    const deleteRoleMutation = useDeleteInternalRole()
    const updateRolePermissionsMutation = useUpdateInternalRole()
    const { data: roleDetailData, isFetching: isFetchingRoleDetail } =
        useInternalRole(editingRole?.id || '')

    useEffect(() => {
        if (usersError) {
            toast.error('Failed to load internal users', {
                description: getErrorMessage(usersError),
            })
        }
    }, [usersError])

    useEffect(() => {
        if (rolesError) {
            toast.error('Failed to load internal roles', {
                description: getErrorMessage(rolesError),
            })
        }
    }, [rolesError])

    const users = useMemo<InternalUser[]>(() => {
        const apiUsers = internalUsersData?.data
        const userList = Array.isArray(apiUsers)
            ? apiUsers
            : Array.isArray((apiUsers as any)?.data)
              ? (apiUsers as any).data
              : []

        return userList.map((user: any) => ({
            id: user.id,
            fullName: user.fullName,
            email: user.email,
            phoneNumber: user.phoneNumber,
            gender: user.gender,
            role:
                user.internalRole?.title ||
                user.effectiveInternalRole ||
                user.role,
            dateAdded: new Date(user.createdAt).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
            }),
            status:
                user.accountStatus === 'INACTIVE'
                    ? 'Inactive'
                    : user.accountStatus === 'SUSPENDED'
                      ? 'Suspended'
                      : 'Active',
            country: user.country,
            residentialAddress: user.residentialAddress,
            internalRoleId: user.internalRole?.id,
            internalRoleTitle: user.internalRole?.title,
            permissions: normalizePermissions(user.internalRole?.permissions),
        }))
    }, [internalUsersData])

    const roles = useMemo<InternalRole[]>(() => {
        const apiRoles = internalRolesData?.data
        const roleList = Array.isArray(apiRoles)
            ? apiRoles
            : Array.isArray((apiRoles as any)?.data)
              ? (apiRoles as any).data
              : []

        return roleList.map((role: any) => ({
            id: role.id,
            title: role.title,
            assignedUsers: role.assignedUsersCount ?? 0,
            isActive: Boolean(role.isActive),
            description: role.description,
            permissions: normalizePermissions(
                (role as any)?.permissions ??
                    (role as any)?.includedPermissions ??
                    []
            ),
        }))
    }, [internalRolesData])

    // const handleUserSearch = (value: string) => {
    //     setUserFilters(prev => ({
    //         ...prev,
    //         search: value || undefined,
    //         page: 1,
    //     }))
    // }

    // const handleRoleSearch = (value: string) => {
    //     setRoleFilters(prev => ({
    //         ...prev,
    //         search: value || undefined,
    //         page: 1,
    //     }))
    // }

    const handleApplyUserFilters = (newFilters: InternalUserFilters) => {
        setUserFilters({ ...newFilters, page: 1, limit: userFilters.limit })
    }

    const handleApplyRoleFilters = (newFilters: InternalRoleFilters) => {
        setRoleFilters({ ...newFilters, page: 1, limit: roleFilters.limit })
    }

    const handleAddUser = (payload: AddUserFormState) => {
        const phoneNumber = payload.phoneNumber.startsWith('+')
            ? payload.phoneNumber
            : `+234${payload.phoneNumber.replace(/^0/, '')}`

        if (!payload.internalRoleId) {
            toast.error('Please select a role for this user')
            return
        }

        createUserMutation.mutate(
            {
                fullName: payload.fullName,
                email: payload.email,
                password: payload.password,
                phoneNumber,
                gender: payload.gender || 'MALE',
                residentialAddress: payload.address,
                country: payload.country,
                internalRoleId: payload.internalRoleId,
            },
            {
                onSuccess: () => {
                    toast.success('Internal user created')
                    setIsAddUserOpen(false)
                    refetchUsers()
                },
                onError: error => {
                    toast.error('Failed to create internal user', {
                        description: getErrorMessage(error),
                    })
                },
            }
        )
    }

    const handleViewUser = (user: InternalUser) => {
        setSelectedUser(user)
        setViewUserOpen(true)
    }

    const handleOpenPermissions = (role: InternalRole) => {
        setSelectedRole(role)
        setIsPermissionOpen(true)
    }

    const handleAddOrEditRole = ({
        title,
        description,
        isActive,
    }: {
        title: string
        description: string
        isActive?: boolean
    }) => {
        if (editingRole) {
            updateRoleMutation.mutate(
                {
                    id: editingRole.id,
                    data: {
                        title,
                        description,
                        ...(isActive !== undefined && { isActive }),
                    },
                },
                {
                    onSuccess: () => {
                        toast.success('Role updated')
                        setEditingRole(null)
                        setIsAddRoleOpen(false)
                        refetchRoles()
                    },
                    onError: error => {
                        toast.error('Failed to update role', {
                            description: getErrorMessage(error),
                        })
                    },
                }
            )
            return
        }

        createRoleMutation.mutate(
            { title, description },
            {
                onSuccess: () => {
                    toast.success('Role created')
                    setIsAddRoleOpen(false)
                    refetchRoles()
                },
                onError: error => {
                    toast.error('Failed to create role', {
                        description: getErrorMessage(error),
                    })
                },
            }
        )
    }

    const handleSavePermissions = (roleId: string, permissions: string[]) => {
        updateRolePermissionsMutation.mutate(
            {
                id: roleId,
                data: { permissions },
            },
            {
                onSuccess: () => {
                    toast.success('Permissions updated')
                    setIsPermissionOpen(false)
                    setSelectedRole(null)
                    refetchRoles()
                },
                onError: error => {
                    toast.error('Failed to update permissions', {
                        description: getErrorMessage(error),
                    })
                },
            }
        )
    }

    const handleDeleteRole = () => {
        if (!roleToDelete) return

        deleteRoleMutation.mutate(roleToDelete.id, {
            onSuccess: () => {
                toast.success('Role deleted')
                setRoleToDelete(null)
                refetchRoles()
            },
            onError: error => {
                toast.error('Failed to delete role', {
                    description: getErrorMessage(error),
                })
            },
        })
    }

    const handleConfirmRevoke = async () => {
        if (usersToRevoke.length === 0) return

        const toastId = toast.loading(
            `Revoking access for ${usersToRevoke.length} user(s)...`
        )

        try {
            await Promise.all(
                usersToRevoke.map(user =>
                    revokeAccessMutation.mutateAsync(user.id)
                )
            )
            toast.success('Access revoked successfully')
            refetchUsers()
        } catch (error) {
            toast.error('Failed to revoke access', {
                description: getErrorMessage(error),
            })
        } finally {
            toast.dismiss(toastId)
            setIsRevokeDialogOpen(false)
            setUsersToRevoke([])
        }
    }

    const userActions = [
        {
            label: 'View account',
            icon: <Eye className="h-4 w-4 text-[#0F9DB5]" />,
            onClick: (selected: InternalUser[]) => {
                if (selected.length === 1) {
                    handleViewUser(selected[0])
                }
            },
            showForSingle: true,
        },
        {
            label: 'Revoke all access',
            icon: <ShieldOff className="h-4 w-4 text-[#FB3748]" />,
            onClick: (selected: InternalUser[]) => {
                if (selected.length === 0) return
                setUsersToRevoke(selected)
                setIsRevokeDialogOpen(true)
            },
        },
    ]

    const roleActions = [
        {
            label: 'Edit role',
            icon: <Wrench className="h-4 w-4 text-[#0F9DB5]" />,
            onClick: (selected: InternalRole[]) => {
                if (selected.length === 1) {
                    setEditingRole(selected[0])
                    setIsAddRoleOpen(true)
                }
            },
            showForSingle: true,
        },
        {
            label: 'Edit permissions',
            icon: <ShieldOff className="h-4 w-4 text-[#1FC16B]" />,
            onClick: (selected: InternalRole[]) => {
                if (selected.length === 1) {
                    handleOpenPermissions(selected[0])
                }
            },
            showForSingle: true,
        },
        {
            label: 'Delete role',
            icon: <Trash2 className="h-4 w-4 text-[#FB3748]" />,
            onClick: (selected: InternalRole[]) => {
                if (selected.length === 1) {
                    setRoleToDelete(selected[0])
                }
            },
            showForSingle: true,
        },
    ]

    const roleOptions = useMemo(
        () => roles.map(role => ({ id: role.id, title: role.title })),
        [roles]
    )

    const roleDetail = roleDetailData?.data
    const modalInitials = editingRole
        ? {
              title: roleDetail?.title ?? editingRole.title ?? '',
              description:
                  roleDetail?.description ?? editingRole.description ?? '',
              isActive: roleDetail?.isActive ?? editingRole.isActive ?? true,
          }
        : { title: '', description: '', isActive: true }

    const roleModalKey = editingRole
        ? `${editingRole.id}-${modalInitials.title}-${modalInitials.description}-${modalInitials.isActive}`
        : 'new-role'

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-[#0F172A]">Users</h1>
                <p className="text-sm text-muted-foreground">
                    Manage all admin user accounts on Ogaboss.
                </p>
            </div>

            <div className="flex flex-wrap gap-8 border-b">
                {[
                    { key: 'users', label: 'Admin Users' },
                    { key: 'roles', label: 'Roles & Permissions' },
                ].map(tab => {
                    const isActive = activeTab === tab.key
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key as TabKey)}
                            className={`relative pb-3 text-sm font-semibold transition-colors ${
                                isActive
                                    ? 'text-[#0F172A]'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {tab.label}
                            {isActive && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-[#17A2B8]" />
                            )}
                        </button>
                    )
                })}
            </div>

            {activeTab === 'users' ? (
                isLoadingUsers ? (
                    renderTableSkeleton()
                ) : (
                    <DataTable
                        columns={adminUserColumns}
                        data={users}
                        searchPlaceholder="Search using name, email or role"
                        // onFilter={() => setIsUserFilterDialogOpen(true)}
                        toolbarActions={
                            <Button
                                onClick={() => setIsAddUserOpen(true)}
                                className="h-10 rounded-full bg-[#0F9DB5] px-4 text-white shadow-sm hover:bg-[#0d8ba1]"
                            >
                                <UserPlus className="h-4 w-4" />
                                Add user
                            </Button>
                        }
                        customActions={userActions}
                        showViewButton={false}
                        viewLabel="View account"
                    />
                )
            ) : isLoadingRoles ? (
                renderTableSkeleton()
            ) : (
                <DataTable
                    columns={roleColumns}
                    data={roles}
                    searchPlaceholder="Search using role ID & title"
                    // onFilter={() => setIsRoleFilterDialogOpen(true)}
                    toolbarActions={
                        <Button
                            onClick={() => {
                                setEditingRole(null)
                                setIsAddRoleOpen(true)
                            }}
                            className="h-10 rounded-full bg-[#0F9DB5] px-4 text-white shadow-sm hover:bg-[#0d8ba1]"
                        >
                            <UserPlus className="h-4 w-4" />
                            Add role
                        </Button>
                    }
                    customActions={roleActions}
                    showViewButton={false}
                    viewLabel="Edit role"
                />
            )}

            <AddUserModal
                open={isAddUserOpen}
                onOpenChange={setIsAddUserOpen}
                onSubmit={handleAddUser}
                roleOptions={roleOptions}
                isSubmitting={createUserMutation.isPending}
            />

            <AddRoleModal
                key={roleModalKey}
                open={isAddRoleOpen}
                onOpenChange={open => {
                    if (!open) setEditingRole(null)
                    setIsAddRoleOpen(open)
                }}
                onSubmit={handleAddOrEditRole}
                initialTitle={modalInitials.title}
                initialDescription={modalInitials.description}
                initialIsActive={modalInitials.isActive}
                isLoadingDetails={isFetchingRoleDetail}
                isSubmitting={
                    editingRole
                        ? updateRoleMutation.isPending
                        : createRoleMutation.isPending
                }
            />

            <PermissionSettingsModal
                key={selectedRole?.id || 'permission-modal'}
                open={isPermissionOpen}
                onOpenChange={open => {
                    setIsPermissionOpen(open)
                    if (!open) setSelectedRole(null)
                }}
                role={selectedRole}
                permissions={permissionsData?.data ?? []}
                onSave={handleSavePermissions}
                isUpdating={updateRolePermissionsMutation.isPending}
            />

            <UserPreviewModal
                user={selectedUser}
                open={viewUserOpen}
                onOpenChange={setViewUserOpen}
            />

            <ConfirmDialog
                open={isRevokeDialogOpen}
                onOpenChange={setIsRevokeDialogOpen}
                title="Revoke all access"
                description={`You are about to revoke access for ${usersToRevoke.length} user(s). This action will immediately disable their access.`}
                confirmLabel="Revoke access"
                onConfirm={handleConfirmRevoke}
                isLoading={revokeAccessMutation.isPending}
                destructive
            />

            <ConfirmDialog
                open={Boolean(roleToDelete)}
                onOpenChange={open => {
                    if (!open) setRoleToDelete(null)
                }}
                title="Delete role"
                description={`Are you sure you want to delete the role "${roleToDelete?.title || ''}"? This action cannot be undone.`}
                confirmLabel="Delete role"
                onConfirm={handleDeleteRole}
                isLoading={deleteRoleMutation.isPending}
                destructive
            />

            <InternalUsersFilterDialog
                open={isUserFilterDialogOpen}
                onOpenChange={setIsUserFilterDialogOpen}
                currentFilters={userFilters}
                onApplyFilters={handleApplyUserFilters}
            />

            <InternalRolesFilterDialog
                open={isRoleFilterDialogOpen}
                onOpenChange={setIsRoleFilterDialogOpen}
                currentFilters={roleFilters}
                onApplyFilters={handleApplyRoleFilters}
            />
        </div>
    )
}

export default InternalUsers
